import { resolvedTenantConfig } from '@app/tenant';
import { MatchingGrade, RuleDryRunSkipReason } from '@app/settings/types';

import { MatchingTier } from '../types';

export const MAX_SELECTED_IPS = 10;

export const MATCHING_ROUTE = '/admin/matching';
// D-4 — step 04's `MatchingRunScreen` owns this route's content. WITHOUT a real route registered
// above `_private.tsx`'s `/admin/matching/*` bounce, it would match `/admin/:entity/:id` and render
// a user-detail page for id "run" (measured, react-router 6.26), not the `*` fallback.
export const MATCHING_LOADER_ROUTE = '/admin/matching/run';

// SAA-184 D12 — display order is per-tenant, slug-keyed; WCS groups VIP/Expedited/Standard, ISC
// keeps VIP/PMP/Standard, and neither renders the other's phantom empty group.
export const TIER_ORDER_BY_TENANT: Record<string, MatchingTier[]> = {
  isc: [MatchingTier.vip, MatchingTier.pmp, MatchingTier.standard],
  wcs: [MatchingTier.vip, MatchingTier.expedited, MatchingTier.standard],
};

export const getTierDisplayOrder = (): MatchingTier[] =>
  TIER_ORDER_BY_TENANT[resolvedTenantConfig.slug] ?? [MatchingTier.vip, MatchingTier.pmp, MatchingTier.standard];

export const MATCHING_POLL_INTERVAL_MS = 2000;
export const MATCHING_STALL_NOTICE_MS = 10 * 60 * 1000;

// Grounded in the design's `PROGRAM_PILL` map (`.dc.html:1205-1209`), NOT its `PROGRAM_COLOR` map
// (`:977`) — that one says PMP and Standard are both `info`, which would paint them identically.
// VIP purple and PMP amber have no theme token; local one-off, same shape as `PriorityTagsCell`'s
// `ORANGE_SX`. A theme entry for a four-value map used on one screen is its own ticket.
export const TIER_LABEL_SX: Record<MatchingTier, { color: string; bgcolor: string }> = {
  [MatchingTier.vip]: { color: '#5B3FBF', bgcolor: '#EDE7FB' },
  [MatchingTier.pmp]: { color: '#A67C00', bgcolor: '#FDF3C8' },
  [MatchingTier.expedited]: { color: '#0E7C7B', bgcolor: '#DAF3F2' },
  [MatchingTier.standard]: { color: 'text.secondary', bgcolor: 'grey.400' },
};

// SAA-236 — the score-breakdown grade chips' design values; darker than the shared `success`/`warning`/
// `error` Label variants, which other screens keep. `clear`/`notExcluded`/`excluded` are unchanged.
export const GRADE_LABEL_SX: Record<MatchingGrade, { color: string; bgcolor: string }> = {
  [MatchingGrade.match]: { color: '#17A667', bgcolor: '#DAF4DD' },
  [MatchingGrade.partial]: { color: '#B07A00', bgcolor: '#FDF3C8' },
  [MatchingGrade.mismatch]: { color: 'error.main', bgcolor: '#FDDDDD' },
  [MatchingGrade.blocking]: { color: 'error.main', bgcolor: '#FDDDDD' },
  [MatchingGrade.excluded]: { color: 'error.main', bgcolor: 'error.light' },
  [MatchingGrade.clear]: { color: 'text.primary', bgcolor: 'grey.400' },
  [MatchingGrade.notExcluded]: { color: 'text.primary', bgcolor: 'grey.400' },
};

// Mirrors `apps/backend/.../match-confirmation/configs/match-confirmation.config.ts` — no
// codegen, so these must be kept in sync by hand (graphql-conventions.md).
export const MATCH_CONFIRMATION_RECIPIENT_MAX = 10;
export const MATCH_CONFIRMATION_SUBJECT_MAX = 200;
export const MATCH_CONFIRMATION_BODY_HTML_MAX = 20_000;

// Restricts the manual-attach dropzone to PDF only (SPEC §4.6). `type` is the free-text `File.type`
// the BE `fileCreateOneWithPreSignedPost` stores (no enum exists there — `file-create.input.ts`).
export const MATCH_CONFIRMATION_ATTACHMENT_FILE_TYPE = 'matchConfirmationAttachment';
export const MATCH_CONFIRMATION_PDF_MIME_TYPE = 'application/pdf';
export const MATCH_CONFIRMATION_PDF_ACCEPT = { [MATCH_CONFIRMATION_PDF_MIME_TYPE]: ['.pdf'] };
export const MATCH_CONFIRMATION_ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

// Engine snake_case → `RuleDryRunSkipReason` enum name (`build-justification-view.ts`'s
// `resolveSkipReason`). Only the matching-family reasons — the rest of the shared enum belongs to
// watchlist/colour and never appears on a matching result.
export const MATCHING_SKIP_REASON_BY_WIRE_VALUE: Record<string, RuleDryRunSkipReason> = {
  rule_disabled: RuleDryRunSkipReason.ruleDisabled,
  model_unavailable: RuleDryRunSkipReason.modelUnavailable,
  source_absent: RuleDryRunSkipReason.sourceAbsent,
  paired_rule_live: RuleDryRunSkipReason.pairedRuleLive,
  no_position_stated: RuleDryRunSkipReason.noPositionStated,
  no_grade_matched: RuleDryRunSkipReason.noGradeMatched,
  // SAA-229: a MODEL `not_applicable` verdict, attributed to the side that actually said nothing.
  gc_source_absent: RuleDryRunSkipReason.gcSourceAbsent,
  insufficient_information: RuleDryRunSkipReason.insufficientInformation,
};
